import { useCallback, useMemo } from 'react'

import { useAccount } from 'wagmi'

import { useBalances } from 'hooks/wallet'
import { useGasFetcher, useGetBalancesRequest } from 'hooks/api'

import {
  useToken,
  useTxsList,
  useGasParams,
  useTotalTokens,
  useTotalGasLimit,
  useIsSelfGenerated,
} from 'state/airdrop/hooks'

import { fromWei } from 'utils/crypto'
import { etherNumbers } from 'constants/variables'

export function useAirdropState() {
  const { address } = useAccount()

  const txs = useTxsList()
  const token = useToken()
  const totalTokens = useTotalTokens()
  const totalGasLimit = useTotalGasLimit()

  const isSelfGenerated = useIsSelfGenerated()

  const balances = useBalances(address)
  const getGasParams = useGasFetcher()
  const balancesRequest = useGetBalancesRequest()

  const gasParams = useGasParams()

  const gasPrice = useMemo(() => {
    const savedGasPrice = gasParams?.maxFeePerGas || gasParams?.gasPrice

    if (savedGasPrice) {
      return BigInt(savedGasPrice)
    }
    return getGasParams.cost || etherNumbers.BG_ZERO
  }, [gasParams, getGasParams.cost])

  const getApproximateCost = useCallback(
    (gasPrice: bigint) => {
      const totalValue = txs.reduce((acc: bigint, curr) => {
        acc += BigInt(curr.value)
        return acc
      }, etherNumbers.BG_ZERO)

      if (isSelfGenerated) {
        const selfGeneratedBump = etherNumbers.SELF_GENERATE_BUMP_GAS * BigInt(txs.length)
        const gasBumpedLimit = totalGasLimit + selfGeneratedBump
        const gasFee = gasPrice * gasBumpedLimit
        const cost = totalValue + gasFee
        const transferCost = etherNumbers.TRANSFER_GAS_LIMIT * gasPrice

        return txs.length > 0 ? cost + transferCost : etherNumbers.BG_ZERO
      }

      const gasFee = gasPrice * totalGasLimit
      const cost = totalValue + gasFee
      return txs.length > 0 ? cost : etherNumbers.BG_ZERO
    },
    [txs, token, totalGasLimit, isSelfGenerated],
  )

  const approximateCost = useMemo(() => {
    return getApproximateCost(gasPrice)
  }, [txs, token, totalGasLimit, isSelfGenerated, gasPrice])

  const totalTokensToDisplay = useMemo(() => fromWei(totalTokens.toString()), [totalTokens])
  const approximateCostToDisplay = useMemo(() => fromWei(approximateCost.toString()), [approximateCost])

  const isTokensBalanceEnough = useMemo(() => {
    if (balancesRequest.isLoading) {
      return true
    }

    return totalTokens <= BigInt(balances.tokenBalance)
  }, [balances.tokenBalance, totalTokens, balancesRequest.isLoading])

  const isBalanceEnough = useMemo(() => {
    if (balancesRequest.isLoading) {
      return true
    }

    return BigInt(balances.nativeBalance) >= approximateCost
  }, [balances.nativeBalance, approximateCost, token?.address, totalTokens, balancesRequest.isLoading])

  return useMemo(
    () => ({
      gasPrice,
      approximateCost,
      isBalanceEnough,
      getApproximateCost,
      totalTokensToDisplay,
      isTokensBalanceEnough,
      approximateCostToDisplay,
    }),
    [gasPrice, approximateCost, isBalanceEnough, totalTokensToDisplay, isTokensBalanceEnough, approximateCostToDisplay],
  )
}
